import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";
var _excluded = ["onClick", "onFocus", "onBlur", "children"];

var _class, _class2, _temp;

import React from 'react';
import { globalObject } from '@skbkontur/global-object';
import { isKonturIcon, isReactUIComponent } from "../../../lib/utils";
import { isIE11, isEdge, isSafari } from "../../../lib/client";
import { keyListener } from "../../../lib/events/keyListener";
import { ThemeContext } from "../../../lib/theming/ThemeContext";
import { CommonWrapper } from "../../../internal/CommonWrapper";
import { cx } from "../../../lib/theming/Emotion";
import { rootNode } from "../../../lib/rootNode";
import { ThemeFactory } from "../../../lib/theming/ThemeFactory";
import { createPropsGetter } from "../../../lib/createPropsGetter";
import { isTheme2022 } from "../../../lib/theming/ThemeHelpers";
import { Link } from "../../Link";
import { styles, activeStyles, globalClasses } from "../Button.styles";
import { ButtonIcon, getButtonIconSizes } from "../ButtonIcon";
import { useButtonArrow } from "../ButtonArrow";
import { getInnerLinkTheme } from "../getInnerLinkTheme";
import { LoadingButtonIcon } from "../LoadingButtonIcon";
/**
 * @deprecated use SizeProp
 */

export var ButtonDataTids = {
  rootElement: 'Button__rootElement',
  root: 'Button__root',
  spinner: 'Button__spinner'
};
export var Button = rootNode(_class = (_temp = _class2 = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(Button, _React$Component);

  function Button() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$Component.call.apply(_React$Component, [this].concat(args)) || this;
    _this.getProps = createPropsGetter(Button.defaultProps);
    _this.state = {
      focusedByTab: false
    };
    _this.node = null;

    _this.handleFocus = function (e) {
      if (!_this.props.disabled && !_this.props.disableFocus) {
        // focus event fires before keyDown eventlistener
        // so we should check tabPressed in async way
        globalObject.requestAnimationFrame == null ? void 0 : globalObject.requestAnimationFrame(function () {
          if (keyListener.isTabPressed) {
            _this.setState({
              focusedByTab: true
            });
          }
        });
        _this.props.onFocus == null ? void 0 : _this.props.onFocus(e);
      }
    };

    _this.handleBlur = function (e) {
      _this.setState({
        focusedByTab: false
      });

      if (!_this.props.disabled && !_this.props.disableFocus) {
        _this.props.onBlur == null ? void 0 : _this.props.onBlur(e);
      }
    };

    _this._ref = function (node) {
      _this.node = node;
    };

    return _this;
  }

  var _proto = Button.prototype;

  _proto.componentDidMount = function componentDidMount() {
    if (this.props.autoFocus) {
      keyListener.isTabPressed = true;
      this.focus();
    }
  };

  Button.getDerivedStateFromProps = function getDerivedStateFromProps(props) {
    if (props.loading || props.disabled) {
      return {
        focusedByTab: false
      };
    }

    return null;
  }
  /**
  * @public
  */
  ;

  _proto.focus = function focus() {
    var _this$node;

    (_this$node = this.node) == null ? void 0 : _this$node.focus();
  }
  /**
  * @public
  */
  ;

  _proto.blur = function blur() {
    var _this$node2;

    (_this$node2 = this.node) == null ? void 0 : _this$node2.blur();
  };

  _proto.render = function render() {
    var _this2 = this;

    return /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, function (theme) {
      _this2.theme = _this2.props.theme ? ThemeFactory.create(_this2.props.theme, theme) : theme;
      return _this2.renderMain();
    });
  };

  _proto.renderLinkRootWithoutHandlers = function renderLinkRootWithoutHandlers(props) {
    var onClick = props.onClick,
        onFocus = props.onFocus,
        onBlur = props.onBlur,
        children = props.children,
        rest = _objectWithoutPropertiesLoose(props, _excluded);

    return /*#__PURE__*/React.createElement("span", rest, children);
  };

  _proto.renderMain = function renderMain() {
    var _cx2, _cx5, _cx6;

    var _this$props = this.props,
        corners = _this$props.corners,
        active = _this$props.active,
        disabled = _this$props.disabled,
        borderless = _this$props.borderless,
        checked = _this$props.checked,
        error = _this$props.error,
        warning = _this$props.warning,
        loading = _this$props.loading,
        narrow = _this$props.narrow,
        icon = _this$props.icon,
        rightIcon = _this$props.rightIcon,
        _noPadding = _this$props._noPadding,
        _noRightPadding = _this$props._noRightPadding,
        visuallyFocused = _this$props.visuallyFocused,
        align = _this$props.align,
        disableFocus = _this$props.disableFocus,
        onMouseEnter = _this$props.onMouseEnter,
        onMouseLeave = _this$props.onMouseLeave,
        onMouseOver = _this$props.onMouseOver,
        onMouseDown = _this$props.onMouseDown,
        onMouseUp = _this$props.onMouseUp,
        onKeyDown = _this$props.onKeyDown,
        onClick = _this$props.onClick,
        onClickCapture = _this$props.onClickCapture,
        width = _this$props.width,
        children = _this$props.children,
        ariaDescribedby = _this$props['aria-describedby'],
        ariaHasPopup = _this$props['aria-haspopup'],
        ariaControls = _this$props['aria-controls'],
        ariaLabel = _this$props['aria-label'],
        ariaChecked = _this$props['aria-checked'],
        ariaExpanded = _this$props['aria-expanded'],
        role = _this$props.role,
        id = _this$props.id;

    var _this$getProps = this.getProps(),
        use = _this$getProps.use,
        type = _this$getProps.type,
        size = _this$getProps.size;

    var sizeClass = this.getSizeClassName();
    var isFocused = this.state.focusedByTab || visuallyFocused;
    var isLink = use === 'link';

    var _isTheme2022 = isTheme2022(this.theme); // eslint-disable-next-line react-hooks/rules-of-hooks


    var _useButtonArrow = useButtonArrow(_extends({}, this.props, {
      isFocused: Boolean(isFocused)
    }), this.theme),
        wrapClassNameWithArrow = _useButtonArrow[0],
        rootClassNameWithArrow = _useButtonArrow[1],
        arrowNode = _useButtonArrow[2];

    var isUseStateWithoutOutlineInDisabledState = !['default', 'backless'].includes(use);
    var rootClassName = '';

    if (_isTheme2022) {
      var trueDisabled = disabled || loading;
      rootClassName = cx.apply(void 0, [styles.root(this.theme), styles[use](this.theme), sizeClass, narrow && styles.narrow(), _noPadding && styles.noPadding(), _noRightPadding && styles.noRightPadding(), rootClassNameWithArrow].concat(trueDisabled ? [styles.disabled(this.theme), isUseStateWithoutOutlineInDisabledState && styles.disabledWithoutOutline(this.theme), checked && styles.checkedDisabled(this.theme), checked && styles.checkedDisabled2022(this.theme), borderless && styles.borderless2022(), use === 'backless' && !checked && styles.backlessDisabled2022(this.theme), use === 'text' && styles.textDisabled2022()] : [active && !checked && activeStyles[use](this.theme), isFocused && styles.focus(this.theme), checked && styles.checked2022(this.theme), checked && isFocused && styles.checkedFocused(this.theme), borderless && !checked && !isFocused && styles.borderless2022()]));
    } else {
      var _cx;

      rootClassName = cx((_cx = {}, _cx[styles.root(this.theme)] = true, _cx[styles.simulatedPress()] = true, _cx[styles[use](this.theme)] = true, _cx[activeStyles[use](this.theme)] = active, _cx[sizeClass] = true, _cx[styles.focus(this.theme)] = isFocused, _cx[styles.checked(this.theme)] = checked, _cx[styles.checkedFocused(this.theme)] = checked && isFocused, _cx[styles.disabled(this.theme)] = disabled || loading, _cx[styles.checkedDisabled(this.theme)] = checked && disabled, _cx[styles.borderless()] = borderless && !disabled && !loading && !checked && !isFocused && !active, _cx[styles.narrow()] = narrow, _cx[styles.noPadding()] = _noPadding, _cx[styles.noRightPadding()] = _noRightPadding, _cx));
    }

    var rootProps = {
      // By default the type attribute is 'submit'. IE8 will fire a click event
      // on this button if somewhere on the page user presses Enter while some
      // input is focused. So we set type to 'button' by default.
      type: type,
      role: role,
      'aria-describedby': ariaDescribedby,
      'aria-haspopup': ariaHasPopup,
      'aria-controls': ariaControls,
      'aria-label': ariaLabel,
      'aria-checked': ariaChecked,
      'aria-expanded': ariaExpanded,
      className: rootClassName,
      style: _extends({
        textAlign: align
      }, corners),
      disabled: disabled || loading,
      onClick: onClick,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      onKeyDown: onKeyDown,
      onMouseEnter: onMouseEnter,
      onMouseLeave: onMouseLeave,
      onMouseOver: onMouseOver,
      onMouseDown: onMouseDown,
      onMouseUp: onMouseUp,
      onClickCapture: onClickCapture,
      tabIndex: disableFocus ? -1 : 0,
      title: this.props.title
    };
    var wrapProps = {
      className: cx(globalClasses.root, (_cx2 = {}, _cx2[styles.wrap(this.theme)] = true, _cx2[wrapClassNameWithArrow] = true, _cx2[this.getSizeWrapClassName()] = true, _cx2)),
      style: {
        width: width
      }
    };
    var innerShadowNode = _isTheme2022 ? null : /*#__PURE__*/React.createElement("div", {
      className: globalClasses.innerShadow
    });
    var outlineNode = null;
    var isDisabled2022 = _isTheme2022 && (disabled || loading);

    if ((!isFocused || isLink) && !isDisabled2022) {
      var _cx3;

      outlineNode = /*#__PURE__*/React.createElement("div", {
        style: {
          zIndex: _isTheme2022 && isLink ? -1 : undefined
        },
        className: cx(styles.outline(), (_cx3 = {}, _cx3[styles.outlineWarning(this.theme)] = warning, _cx3[styles.outlineError(this.theme)] = error, _cx3[styles.outlineLink()] = isLink, _cx3[styles.outlineLinkWarning(this.theme)] = isLink && warning, _cx3[styles.outlineLinkError(this.theme)] = isLink && error, _cx3))
      });
    }

    var iconProps = {
      use: use,
      size: this.getProps().size,
      hasChildren: !!children,
      loading: loading || false
    };
    var leftIconNode = icon && /*#__PURE__*/React.createElement(ButtonIcon, _extends({}, iconProps, {
      position: "left",
      icon: icon
    }));
    var rightIconNode = rightIcon && /*#__PURE__*/React.createElement(ButtonIcon, _extends({}, iconProps, {
      hasBothIcons: !!icon && !!rightIcon,
      position: "right",
      icon: rightIcon
    })); // Force disable all props and features, that cannot be use with Link

    if (isLink) {
      var _cx4;

      rootProps.className = cx((_cx4 = {}, _cx4[styles.root(this.theme)] = true, _cx4[sizeClass] = true, _cx4[styles.link(this.theme)] = true, _cx4[styles.linkLineHeight()] = !isSafari || isSafari && !_isTheme2022, _cx4[styles.linkLineHeightSafariFallback()] = isSafari && _isTheme2022, _cx4[styles.linkFocus(this.theme)] = isFocused, _cx4[styles.linkDisabled(this.theme)] = disabled || loading, _cx4));
      Object.assign(wrapProps, {
        className: cx(styles.wrap(this.theme), styles.wrapLink()),
        style: {
          width: wrapProps.style.width
        }
      });
      rootProps.style.textAlign = undefined;
    }

    var hasLoadingNode = loading && !icon && !rightIcon;
    var loadingNode = hasLoadingNode && /*#__PURE__*/React.createElement(LoadingButtonIcon, {
      size: size
    });
    var captionNode = /*#__PURE__*/React.createElement("div", {
      className: cx(styles.caption(), globalClasses.caption, (_cx5 = {}, _cx5[styles.captionTranslated()] = (active || checked) && !loading && !_isTheme2022, _cx5[styles.captionLink()] = isLink, _cx5[styles.captionDisabled()] = !checked && disabled, _cx5))
    }, loadingNode, leftIconNode, /*#__PURE__*/React.createElement("span", {
      className: cx(globalClasses.text, (_cx6 = {}, _cx6[styles.visibilityHidden()] = hasLoadingNode, _cx6))
    }, children), rightIconNode);

    if (_isTheme2022 && isLink && !loading) {
      captionNode = /*#__PURE__*/React.createElement(ThemeContext.Provider, {
        value: getInnerLinkTheme(this.theme)
      }, /*#__PURE__*/React.createElement(Link, {
        focused: isFocused,
        disabled: disabled,
        icon: this.renderIcon2022(icon),
        rightIcon: this.renderIcon2022(rightIcon),
        as: this.renderLinkRootWithoutHandlers,
        tabIndex: -1
      }, children));
    }

    return /*#__PURE__*/React.createElement(CommonWrapper, _extends({
      rootNodeRef: this.setRootNode
    }, this.props), /*#__PURE__*/React.createElement("span", _extends({}, wrapProps, {
      "data-tid": ButtonDataTids.rootElement
    }), /*#__PURE__*/React.createElement("button", _extends({
      id: id,
      "data-tid": ButtonDataTids.root,
      ref: this._ref
    }, rootProps), innerShadowNode, outlineNode, arrowNode, captionNode)));
  };

  _proto.renderIcon2022 = function renderIcon2022(icon) {
    if (icon && isKonturIcon(icon)) {
      var _icon$props$size;

      var sizes = getButtonIconSizes(this.theme);
      return /*#__PURE__*/React.cloneElement(icon, {
        size: (_icon$props$size = icon.props.size) != null ? _icon$props$size : sizes[this.getProps().size]
      });
    }

    return icon;
  };

  _proto.getSizeClassName = function getSizeClassName() {
    var _cx7, _cx8, _cx9;

    var _isTheme2022 = isTheme2022(this.theme);

    switch (this.getProps().size) {
      case 'large':
        return cx(styles.sizeLarge(this.theme), (_cx7 = {}, _cx7[styles.sizeLargeIE11(this.theme)] = isIE11 || isEdge, _cx7[styles.sizeLargeWithIcon(this.theme)] = !!this.props.icon, _cx7[styles.sizeLargeWithIconWithoutText(this.theme)] = _isTheme2022 && !!this.props.icon && !this.props.children, _cx7));

      case 'medium':
        return cx(styles.sizeMedium(this.theme), (_cx8 = {}, _cx8[styles.sizeMediumIE11(this.theme)] = isIE11 || isEdge, _cx8[styles.sizeMediumWithIcon(this.theme)] = !!this.props.icon, _cx8[styles.sizeMediumWithIconWithoutText(this.theme)] = _isTheme2022 && !!this.props.icon && !this.props.children, _cx8));

      case 'small':
      default:
        return cx(styles.sizeSmall(this.theme), (_cx9 = {}, _cx9[styles.sizeSmallIE11(this.theme)] = isIE11 || isEdge, _cx9[styles.sizeSmallWithIcon(this.theme)] = !!this.props.icon, _cx9[styles.sizeSmallWithIconWithoutText(this.theme)] = _isTheme2022 && !!this.props.icon && !this.props.children, _cx9));
    }
  };

  _proto.getSizeWrapClassName = function getSizeWrapClassName() {
    switch (this.getProps().size) {
      case 'large':
        return styles.wrapLarge(this.theme);

      case 'medium':
        return styles.wrapMedium(this.theme);

      case 'small':
      default:
        return styles.wrapSmall(this.theme);
    }
  };

  return Button;
}(React.Component), _class2.__KONTUR_REACT_UI__ = 'Button', _class2.displayName = 'Button', _class2.__BUTTON__ = true, _class2.defaultProps = {
  use: 'default',
  size: 'small',
  type: 'button'
}, _temp)) || _class;
export var isButton = isReactUIComponent('Button');